import * as THREE from 'three';
import { chamferBox, plainBox, mergeGeom, place, translate, rotate, forgeMaterial } from './primitives.js';

/**
 * Gloved hands.
 *
 * A floating weapon with no hands is the clearest sign of an unfinished viewmodel —
 * more so than any amount of missing surface detail, because the brain expects to see
 * how the object is being held. These are built once and posed by the caller.
 *
 * Canonical hand space (before the caller rotates it into place):
 *   origin  = wrist joint
 *   +Z      = toward the knuckles
 *   +Y      = back of the hand (palm faces -Y)
 *   +X      = thumb side for a RIGHT hand (mirrored for the left)
 *
 * `curl` (0..1) closes the fingers toward -Y, so a curl of ~0.85 wraps a grip and
 * ~0.25 lies flat along a handguard.
 */

// Gloves are the closest thing to the camera in the whole scene, so they need a much
// finer texel density than the weapon or the stipple reads as gravel.
const GLOVE_UV = 0.020;

const FINGER = {
  // [length, width, thickness] per phalanx, proximal -> distal
  segs: [
    [0.042, 0.0195, 0.0200],
    [0.027, 0.0180, 0.0182],
    [0.021, 0.0163, 0.0165],
  ],
  // Base curl per joint at curl = 1.
  bend: [1.05, 1.28, 0.92],
};

// Finger root positions across the knuckle row (x, z-offset, length scale).
// +X is the thumb side, so the index finger sits at +X next to the thumb and the
// little finger at -X. Getting this backwards puts the thumb beside the pinky, which
// is the sort of thing nobody can name but everybody can see.
const FINGERS = [
  { x: 0.0315, z: 0.0, scale: 0.94, phase: 0.00 }, // index
  { x: 0.0105, z: 0.004, scale: 1.0, phase: 0.06 },
  { x: -0.0105, z: 0.001, scale: 0.96, phase: 0.10 },
  { x: -0.0315, z: -0.005, scale: 0.85, phase: 0.16 }, // little
];

/**
 * @param {object} ctx GameContext (for forge materials)
 * @param {object} opts
 *   side:   1 for right, -1 for left
 *   curl:   0..1 overall finger closure
 *   spread: extra splay between fingers
 *   trigger: if set, the index finger uses this curl instead (for the trigger hand)
 *   thumbTuck: 0..1
 *   cuff:   draw the sleeve cuff
 */
export function buildHand(ctx, opts = {}) {
  const side = opts.side ?? 1;
  const curl = opts.curl ?? 0.8;
  const spread = opts.spread ?? 0;
  const thumbTuck = opts.thumbTuck ?? 0.7;

  // The forge's polymer albedo is a near-black weapon furniture tone (~0.06 linear).
  // Tinting it cannot make it lighter — a colour multiplier only ever darkens — so a
  // gloved hand built on it comes out as a featureless black brick next to the
  // receiver. Dropping the albedo map and driving the colour directly keeps the
  // normal and roughness detail while letting the glove sit at a readable mid tone,
  // which is what lets the fingers separate from each other at gameplay distance.
  const gloveMat = forgeMaterial(ctx, 'polymer', {
    map: null,
    color: 0x646a5c,
    roughness: 0.88,
    metalness: 0.0,
  });
  const padMat = forgeMaterial(ctx, 'polymer', { map: null, color: 0x33362f, roughness: 0.72 });
  const cuffMat = forgeMaterial(ctx, 'polymer', { map: null, color: 0x50553f, roughness: 0.95 });

  const group = new THREE.Group();

  /* ------------------------------- palm block ------------------------------- */
  const palmGeoms = [];
  // Slight wedge: the palm is thicker at the knuckles than at the wrist.
  palmGeoms.push(place(chamferBox(0.081, 0.030, 0.052, 0.0035, GLOVE_UV), 0, 0.001, 0.048));
  palmGeoms.push(place(chamferBox(0.070, 0.027, 0.036, 0.0032, GLOVE_UV), 0, 0.0, 0.016));
  // Thenar mound (the fleshy pad below the thumb) — the shape that stops a hand
  // reading as a rectangle.
  palmGeoms.push(place(chamferBox(0.024, 0.026, 0.044, 0.004, GLOVE_UV), side * 0.026, -0.004, 0.030));
  const palm = new THREE.Mesh(mergeGeom(palmGeoms), gloveMat);
  group.add(palm);

  /* ------------------------------ knuckle pads ------------------------------ */
  const pads = [];
  for (const f of FINGERS) {
    pads.push(place(chamferBox(0.019, 0.006, 0.019, 0.0018, GLOVE_UV), side * f.x, 0.0165, 0.068 + f.z));
  }
  pads.push(place(chamferBox(0.062, 0.005, 0.028, 0.002, GLOVE_UV), 0, 0.0165, 0.038));
  group.add(new THREE.Mesh(mergeGeom(pads), padMat));

  /* -------------------------------- fingers --------------------------------- */
  for (let i = 0; i < FINGERS.length; i++) {
    const f = FINGERS[i];
    const c = i === 0 && opts.trigger != null ? opts.trigger : Math.min(1.25, curl + f.phase * 0.35);
    const finger = buildFinger(gloveMat, padMat, f.scale, c);
    finger.position.set(side * f.x, 0.002, 0.074 + f.z);
    finger.rotation.z = -side * (f.x * 3.2 + spread * (i - 1.5) * 0.06);
    group.add(finger);
  }

  /* --------------------------------- thumb ---------------------------------- */
  const thumb = new THREE.Group();
  const t1 = new THREE.Mesh(chamferBox(0.023, 0.023, 0.040, 0.0035, GLOVE_UV), gloveMat);
  t1.position.z = 0.020;
  thumb.add(t1);
  const t2j = new THREE.Group();
  t2j.position.z = 0.040;
  t2j.rotation.y = -side * (0.35 + thumbTuck * 0.75);
  const t2 = new THREE.Mesh(chamferBox(0.020, 0.020, 0.034, 0.0032, GLOVE_UV), gloveMat);
  t2.position.z = 0.017;
  t2j.add(t2);
  thumb.add(t2j);
  thumb.position.set(side * 0.034, -0.004, 0.026);
  thumb.rotation.set(0.25, -side * (0.55 + thumbTuck * 0.35), side * 0.30);
  group.add(thumb);

  /* ---------------------------- wrist + sleeve ------------------------------ */
  if (opts.cuff !== false) {
    // Kept deliberately slim and short. An anatomically sized forearm block is wider
    // than the receiver and, seen from any angle where the fingers are hidden, reads
    // as a detached brick floating beside the weapon — which is exactly what it
    // looked like before. In first person the arm is off-frame anyway; all this has
    // to do is close off the wrist.
    const cuffParts = [];
    cuffParts.push(place(chamferBox(0.056, 0.044, 0.024, 0.0035, GLOVE_UV), 0, -0.001, -0.013));
    cuffParts.push(place(chamferBox(0.062, 0.050, 0.014, 0.0040, GLOVE_UV), 0, -0.002, -0.031));
    cuffParts.push(place(chamferBox(0.058, 0.048, 0.052, 0.0045, GLOVE_UV), 0, -0.003, -0.062));
    group.add(new THREE.Mesh(mergeGeom(cuffParts), cuffMat));
    // Velcro strap: a raised band that catches light across the wrist.
    const strap = new THREE.Mesh(chamferBox(0.060, 0.048, 0.007, 0.0014, GLOVE_UV), padMat);
    strap.position.set(0, -0.001, -0.024);
    group.add(strap);
  }

  group.traverse((o) => {
    if (o.isMesh) {
      o.castShadow = false;
      o.receiveShadow = false;
      o.frustumCulled = false;
    }
  });
  return group;
}

function buildFinger(mat, padMat, scale, curl) {
  const root = new THREE.Group();
  let parent = root;
  for (let s = 0; s < FINGER.segs.length; s++) {
    const [len, w, t] = FINGER.segs[s];
    const L = len * scale;
    const joint = new THREE.Group();
    joint.rotation.x = FINGER.bend[s] * curl;
    parent.add(joint);

    const seg = new THREE.Mesh(chamferBox(w * scale, t * scale, L, 0.0026, GLOVE_UV), mat);
    seg.position.z = L / 2;
    joint.add(seg);

    // A darker pad over each knuckle sells the glove.
    if (s < 2) {
      const knuckle = new THREE.Mesh(plainBox(w * scale * 0.86, 0.0035, w * scale * 0.9, GLOVE_UV), padMat);
      knuckle.position.set(0, t * scale * 0.5 + 0.0012, L * 0.16);
      joint.add(knuckle);
    }

    const next = new THREE.Group();
    next.position.z = L;
    joint.add(next);
    parent = next;
  }
  return root;
}

/* -------------------------------------------------------------------------- */
/* mounting a hand on a weapon                                                 */
/* -------------------------------------------------------------------------- */

/**
 * Hand orientation is specified as a pair of body axes rather than Euler angles.
 *
 * Euler triples for a wrist are unreadable and impossible to reason about: there is
 * no way to look at `(1.16, -0.10, 0.06)` and know whether the thumb ends up over the
 * grip or under it. Naming the two axes that actually matter — where the knuckles are
 * relative to the wrist (`zAxis`) and which way the thumb points (`xAxis`) — makes
 * each pose checkable against the weapon it is holding.
 *
 *   zAxis  wrist -> knuckles
 *   xAxis  thumb side of the hand (the thumb itself sits at +X for a right hand, -X
 *          for a left one, so a left hand's thumb points along -xAxis)
 *
 * Fingers always curl toward -Y, and Y is derived, so the palm automatically ends up
 * facing whatever the fingers close onto.
 */
export const GRIPS = {
  // Firing hand on a pistol grip: palm on the right rear of the grip, thumb over the
  // top, fingers wrapping forward and left around the front strap. The metacarpals
  // are kept near-horizontal so the forearm exits back and only slightly up — steeper
  // than this and the wrist drives straight through the stock.
  pistol: { zAxis: [-0.12, -0.16, -0.98], xAxis: [0, 0.99, -0.16], offset: [0.0342, 0.0056, 0.0343] },
  /**
   * Support hand on a handguard: wrist low and left, knuckles running *forward* along
   * the handguard with the fingers closing inboard around it.
   *
   * THIS IS THE POSE THAT BLOCKED EVERY SIGHT IN THE GAME. It used to be a full
   * thumb-over C-clamp (`zAxis: [0.10, 0.92, -0.38]`), with the knuckle row standing
   * 64 mm above the wrist. Measured by raycasting the sight line, that put glove
   * geometry at model y = 0.0546 on a weapon whose optical axis is at 0.0547 — so the
   * index knuckles sat dead centre in the sight picture on every weapon, and the clear
   * aperture at dead centre was exactly 0.00 degrees.
   *
   * A hand belongs *under* the line of sight, not in it: the knuckle axis leans
   * forward (y component 0.92 -> 0.30) so the whole hand lies along the handguard and
   * clears the bore by more than a centimetre. Do not raise the y component of `zAxis`
   * without re-measuring the clear aperture of all twenty-three weapons.
   */
  support: { zAxis: [0.26, 0.30, -0.92], xAxis: [0, -0.92, -0.30], offset: [-0.0236, -0.0093, 0.0284] },
  /**
   * Support hand wrapped around a vertical foregrip, fist closed: knuckles point
   * *down* the grip rather than forward along a tube. Used where the weapon has a
   * genuine vertical grip, which sits the hand lower still than the handguard pose.
   */
  vertical: { zAxis: [0.10, -0.94, -0.32], xAxis: [0, -0.32, 0.94], offset: [-0.0300, 0.0180, 0.0250] },
  /**
   * Support hand on a pump forend or a wide wooden handguard: same forward lie as
   * `support` but with the wrist rolled flatter, because the hand is riding a fat
   * cylinder rather than pinching a slim rail.
   */
  pump: { zAxis: [0.20, 0.18, -0.96], xAxis: [0, -0.97, -0.18], offset: [-0.0250, -0.0120, 0.0290] },
};

const _bx = new THREE.Vector3();
const _by = new THREE.Vector3();
const _bz = new THREE.Vector3();
const _bm = new THREE.Matrix4();

function orientHand(obj, zAxis, xAxis) {
  _bz.fromArray(zAxis).normalize();
  _bx.fromArray(xAxis);
  _bx.addScaledVector(_bz, -_bx.dot(_bz)).normalize(); // Gram-Schmidt against Z
  _by.crossVectors(_bz, _bx).normalize();
  _bm.makeBasis(_bx, _by, _bz);
  obj.setRotationFromMatrix(_bm);
}

/**
 * Attach both hands to a built weapon.
 *
 * `anchor` is the point on the weapon being held; the wrist is offset from it by the
 * grip's own palm-to-wrist vector, so poses are authored against geometry rather than
 * by nudging numbers until the screenshot looks right. The mount group is what the
 * animation layer moves; the hand keeps its rest pose inside it.
 */
export function attachHands(ctx, root, parts, cfg) {
  const out = {};
  for (const side of ['right', 'left']) {
    const c = cfg[side];
    if (!c) continue;
    const hand = buildHand(ctx, {
      side: side === 'right' ? 1 : -1,
      curl: c.curl,
      spread: c.spread ?? 0,
      trigger: c.trigger,
      thumbTuck: c.thumbTuck ?? 0.7,
    });
    const grip = GRIPS[c.grip ?? (side === 'right' ? 'pistol' : 'support')];
    orientHand(hand, c.zAxis ?? grip.zAxis, c.xAxis ?? grip.xAxis);
    const o = grip.offset;
    hand.position.set(c.anchor[0] + o[0], c.anchor[1] + o[1], c.anchor[2] + o[2]);
    const mount = new THREE.Group();
    // Named so the sight-line raycast can tell a glove from the weapon. The support
    // hand blocking the optic is the single most expensive bug this project has
    // shipped, and "which mesh did the ray hit" is the question that found it.
    mount.name = side + 'HandMount';
    hand.name = side + 'Hand';
    mount.add(hand);
    const host = c.parent && parts[c.parent] ? parts[c.parent] : root;
    host.add(mount);
    out[side] = mount;
    out[side + 'Hand'] = hand;
  }
  parts.leftHandMount = out.left;
  parts.rightHandMount = out.right;
  return out;
}

/** A bare forearm/sleeve stub used where only the arm enters frame. */
export function buildSleeve(ctx, length = 0.16) {
  const mat = forgeMaterial(ctx, 'polymer', { color: 0x434838, roughness: 0.95 });
  const parts = [];
  parts.push(translate(chamferBox(0.078, 0.066, length, 0.006, GLOVE_UV), 0, 0, -length / 2));
  parts.push(rotate(chamferBox(0.084, 0.072, 0.018, 0.004, GLOVE_UV), 0, 0, 0));
  const m = new THREE.Mesh(mergeGeom(parts), mat);
  m.frustumCulled = false;
  return m;
}
